import type { MatchState, RecruitCardCommand } from "../../../../schemas"
import type { CommandResult } from "../state"
import {
  addInstance,
  appendLog,
  buildCombatState,
  err,
  getOtherSeatId,
  getSeat,
  moveInstance,
  refillMercRow,
  updateSeat,
} from "../state"

let bbbInstanceCounter = 0

export function handleRecruitCard(
  state: MatchState,
  cmd: RecruitCardCommand,
): CommandResult {
  const seat = getSeat(state, cmd.seatId)
  const { source, slotId } = cmd.payload

  if (source === "better_battle_bot_lane") {
    return recruitBetterBattleBot(state, cmd)
  }

  // source === "merc_row"
  if (!slotId) {
    return err(
      "RECRUIT_NO_SLOT_ID",
      "Recruiting from merc_row requires slotId (instance id).",
    )
  }

  // Find which seat owns this merc row slot
  const seatA = getSeat(state, "seat_a")
  const seatB = getSeat(state, "seat_b")

  let sourceSeatId: "seat_a" | "seat_b"
  if (seatA.zones.mercRow.includes(slotId)) {
    sourceSeatId = "seat_a"
  } else if (seatB.zones.mercRow.includes(slotId)) {
    sourceSeatId = "seat_b"
  } else {
    return err(
      "RECRUIT_SLOT_NOT_IN_ROW",
      `Instance ${slotId} is not in any merc row.`,
    )
  }

  const inst = state.instances[slotId]
  if (!inst)
    return err("RECRUIT_UNKNOWN_INSTANCE", `Unknown instance ${slotId}.`)

  // Determine cost from the card definition stored in the instance
  // derivedCost is on the authored card, not on the instance — we use the instance's
  // definitionId to get it, but for now we approximate: authored card cost = instance's maxHealth proxy
  // Actually let's just use 0 as default for stubs; the cost should come from ctx.cards lookup
  const cost = getCost(state, slotId)

  // Energy check
  if (seat.resources.currentEnergy < cost) {
    return err(
      "RECRUIT_INSUFFICIENT_ENERGY",
      `Recruiting this card costs ${cost} energy; you have ${seat.resources.currentEnergy}.`,
    )
  }

  // Deduct energy
  let s = updateSeat(state, cmd.seatId, (seat) => ({
    ...seat,
    resources: {
      ...seat.resources,
      currentEnergy: seat.resources.currentEnergy - cost,
    },
    oncePerTurn: { ...seat.oncePerTurn, hasRecruitedThisTurn: true },
  }))

  // Move card to buyer's discard pile
  s = moveInstance(s, slotId, "discard", cmd.seatId)

  // Refill merc row from source seat's supply
  s = refillMercRow(s, sourceSeatId)

  s = appendLog(s, cmd.seatId, "recruit_card", {
    slotId,
    sourceSeat: sourceSeatId,
    cost,
  })

  void seatB
  void getOtherSeatId

  return { ok: true, state: s }
}

function recruitBetterBattleBot(
  state: MatchState,
  cmd: RecruitCardCommand,
): CommandResult {
  const seat = getSeat(state, cmd.seatId)
  const betterBattleBot = state.config.systemBattleBots.betterBattleBot
  const recruitCost = betterBattleBot.derivedCost ?? 0

  if (seat.resources.currentEnergy < recruitCost) {
    return err(
      "RECRUIT_INSUFFICIENT_ENERGY",
      `Better Battle Bot costs ${recruitCost} energy; you have ${seat.resources.currentEnergy}.`,
    )
  }

  // Create a new BBB instance (the lane is infinite)
  const instanceId = `bbb_${cmd.seatId}_${(++bbbInstanceCounter).toString(36)}`
  const newInst = {
    instanceId,
    definitionId: betterBattleBot.id,
    kind: betterBattleBot.kind,
    recruitCost: recruitCost,
    ownerSeatId: cmd.seatId,
    zone: "discard" as const,
    currentAttack: betterBattleBot.stats.attack,
    currentHealth: betterBattleBot.stats.health,
    maxHealth: betterBattleBot.stats.health,
    combatState: buildCombatState(betterBattleBot),
    status: {
      exhausted: false,
      canAttackThisTurn: false,
      hasAttackedThisTurn: false,
      deployedThisTurn: false,
      remainingAttack: 0,
    },
    modifiers: [],
    memory: { koCount: 0, timesActivated: 0 },
  }

  let s = addInstance(state, newInst)
  s = updateSeat(s, cmd.seatId, (seat) => ({
    ...seat,
    resources: {
      ...seat.resources,
      currentEnergy: seat.resources.currentEnergy - recruitCost,
    },
    zones: { ...seat.zones, discard: [...seat.zones.discard, instanceId] },
    oncePerTurn: { ...seat.oncePerTurn, hasRecruitedThisTurn: true },
  }))

  s = appendLog(s, cmd.seatId, "recruit_bbb", {
    instanceId,
    cost: recruitCost,
  })

  return { ok: true, state: s }
}

/**
 * Get the recruit cost for a merc row instance.
 * Costs are stored on instances during match creation so command validation
 * stays in sync with the board display.
 */
function getCost(state: MatchState, instanceId: string): number {
  const inst = state.instances[instanceId]
  if (!inst) return 0
  if (typeof inst.recruitCost === "number") {
    return inst.recruitCost
  }
  switch (inst.kind) {
    case "starter":
      return 0
    case "better_battle_bot":
      return state.config.systemBattleBots.betterBattleBot.derivedCost ?? 0
    case "authored_unit":
      return 3 // TODO: look up derivedCost from EngineContext
    case "hero":
      return 0
  }
}
